import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useDueDateTypesApi } from "../../../http/dueDateTypes/api";
import { DueDateType } from "../../../http/dueDateTypes/types";
import { PaginationQueryWithFields } from "../../../types";
import { isWithinLast48h } from "../../../types/utils";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import NewItemIcon from "../../ui/NewItemIcon";
import Table from "../../ui/Table";
import CreateDueDateTypeButton from "./CreateDueDateTypeButton";
import DeleteDueDateTypeDialog from "./DeleteDueDateTypeDialog";
import DueDateTypeHistoryDialog from "./DueDateTypeHistoryDialog";
import EditDueDateTypeDialog from "./EditDueDateTypeDialog";

const DUE_DATE_TYPE_FIELDS = ["id", "createdAt", "name", "versionNumber"] as const;
type DueDateTypeField = (typeof DUE_DATE_TYPE_FIELDS)[number];
type DueDateTypeSearch = Pick<DueDateType, DueDateTypeField>;

interface DueDateTypesProps {
  onClose: () => void;
}

const DueDateTypes: FC<DueDateTypesProps> = ({ onClose }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const dueDateTypesApi = useDueDateTypesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "dd" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [itemToEdit, setItemToEdit] = useState<DueDateTypeSearch>();
  const [itemToDelete, setItemToDelete] = useState<DueDateTypeSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<DueDateTypeSearch>();

  const query: PaginationQueryWithFields<DueDateType> = {
    ...filters.state,
    q: debouncedValue,
    fields: DUE_DATE_TYPE_FIELDS,
  };

  const result = dueDateTypesApi.useAdminDueDateTypes(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: DueDateTypeSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.dueDateTypes.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.dueDateTypes.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.dueDateTypes.plural}
        preTitleButton={userSettings.permissions.dueDateTypes.insert && <CreateDueDateTypeButton />}
      >
        <Table<DueDateTypeSearch>
          filters={filters}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="type.name" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.name}
              </td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToDelete && (
        <DeleteDueDateTypeDialog dueDateTypeId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && <EditDueDateTypeDialog dueDateTypeId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />}

      {!!itemToShowVersions && (
        <DueDateTypeHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );
};

export default DueDateTypes;
